import { useEditPanelViewport } from '../../ui/use-edit-panel-viewport.js';
import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { AdminApiError } from '../../lib/api-client.js';
import { Button, Field, TextInput } from '../../ui/index.js';
import './SessionGate.css';
import { StartupScreen } from './StartupScreen.js';

let scrollLocks = 0;
let originalOverflow = '';
let originalOverflowPriority = '';

export type SessionPhase =
  | 'checking'
  | 'ready'
  | 'expired'
  | 'unavailable'
  | 'forbidden'
  | 'logout'
  | 'logout-error'
  | 'signed-out';
export function SessionGate({
  phase,
  modal,
  onLogin,
  onRetry,
  onLogout,
}: {
  phase: Exclude<SessionPhase, 'ready'>;
  modal: boolean;
  onLogin(email: string, password: string): Promise<void>;
  onRetry(): void;
  onLogout(): void;
}) {
  const id = useId();
  const node = useRef<HTMLDialogElement>(null);
  const { panelRef, bodyRef } = useEditPanelViewport(true);
  const capturePanel = useCallback(
    (value: HTMLDialogElement | null) => {
      node.current = value;
      panelRef(value);
    },
    [panelRef],
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const gate = useRef(false);
  const [error, setError] = useState('');
  const login = phase === 'expired' || phase === 'signed-out';
  const loading = phase === 'checking' || phase === 'logout';
  useLayoutEffect(() => {
    if (scrollLocks++ === 0) {
      originalOverflow = document.body.style.getPropertyValue('overflow');
      originalOverflowPriority = document.body.style.getPropertyPriority('overflow');
      document.body.style.setProperty('overflow', 'hidden');
    }
    return () => {
      if (--scrollLocks === 0) {
        if (originalOverflow)
          document.body.style.setProperty('overflow', originalOverflow, originalOverflowPriority);
        else document.body.style.removeProperty('overflow');
      }
    };
  }, []);
  useLayoutEffect(() => {
    if (!login) {
      setPassword('');
      setError('');
    }
  }, [login]);
  useLayoutEffect(() => {
    const dialog = node.current;
    if (!dialog) return;
    if (modal) dialog.showModal();
    else dialog.show();
    return () => dialog.close();
  }, [modal]);
  return (
    <dialog
      ref={capturePanel}
      className={`admin-session-gate diana-ui-portal${loading ? ' admin-session-gate--loading' : ''}`}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      onCancel={(event) => event.preventDefault()}
    >
      {loading ? (
        <StartupScreen
          title={phase === 'checking' ? 'Opening your studio' : 'Signing out…'}
          description={phase === 'checking' ? 'Checking your session…' : 'Finishing securely…'}
          titleId={`${id}-title`}
          descriptionId={`${id}-description`}
        />
      ) : (
        <header className="admin-session-gate__header">
          <p className="admin-session-gate__brand">PEROVA PHOTO · ADMIN</p>
          <h1 id={`${id}-title`}>
            {phase === 'logout-error'
              ? 'Sign-out not confirmed'
              : phase === 'unavailable'
                ? 'Could not connect'
                : phase === 'forbidden'
                  ? 'Access unavailable'
                  : phase === 'expired'
                    ? 'Sign in again'
                    : 'Welcome back'}
          </h1>
          <p id={`${id}-description`}>
            {phase === 'expired'
              ? 'Your open draft is kept while this app stays open.'
              : phase === 'unavailable'
                ? 'Check your connection and try again. Nothing will be saved automatically.'
                : phase === 'logout-error'
                  ? 'Your data is locked. Reconnect and retry signing out.'
                  : phase === 'forbidden'
                    ? 'This account cannot access the admin. Contact the owner.'
                    : 'Sign in to continue.'}
          </p>
        </header>
      )}
      {login ? (
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            if (gate.current) return;
            gate.current = true;
            setBusy(true);
            setError('');
            try {
              await onLogin(email, password);
              setPassword('');
            } catch (cause) {
              setError(
                cause instanceof AdminApiError && cause.status === 429
                  ? 'Too many attempts. Wait a moment and try again.'
                  : cause instanceof AdminApiError && cause.kind === 'unauthorized'
                    ? 'Email or password is incorrect.'
                    : cause instanceof AdminApiError && cause.kind === 'forbidden'
                      ? 'This account cannot access the admin.'
                      : modal
                        ? 'Could not connect. Your draft is kept. Try again.'
                        : 'Could not connect. Try again.',
              );
            } finally {
              gate.current = false;
              setBusy(false);
            }
          }}
        >
          <div ref={bodyRef} className="admin-session-gate__fields">
            <Field label="Email" htmlFor={`${id}-email`}>
              <TextInput
                id={`${id}-email`}
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <Field label="Password" htmlFor={`${id}-password`}>
              <TextInput
                id={`${id}-password`}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            {error ? <p role="alert">{error}</p> : null}
          </div>
          <footer>
            <Button variant="primary" type="submit" loading={busy}>
              Sign in
            </Button>
          </footer>
        </form>
      ) : null}
      {phase === 'unavailable' || phase === 'forbidden' ? (
        <Button variant="primary" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
      {phase === 'logout-error' ? (
        <Button variant="primary" onClick={onLogout}>
          Retry sign out
        </Button>
      ) : null}
      {modal && !['logout', 'logout-error', 'checking'].includes(phase) ? (
        <Button variant="text" disabled={busy} onClick={onLogout}>
          Sign out
        </Button>
      ) : null}
    </dialog>
  );
}
